import { Link } from '@inertiajs/react'
import { AppStoreLinks } from '@/components/AppStoreLinks'
import { EVENTS, trackEvent } from '@/lib/posthog'
import { MessageCircle, Menu, X } from 'lucide-react'
import { useState } from 'react'

interface MarketingLayoutProps {
  children: React.ReactNode
  appUrl?: string
}

export function MarketingLayout({ children, appUrl }: MarketingLayoutProps) {
  return (
    <div className="min-h-screen bg-background bg-[radial-gradient(circle_at_top,_rgba(185,255,74,0.18),_transparent_55%)]">
      <Header appUrl={appUrl} />
      <main>{children}</main>
      <Footer />
    </div>
  )
}

function Header({ appUrl }: { appUrl: string }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img src="/icon.png" alt="Tippy" className="h-8 w-8 rounded-lg" />
            <span className="text-lg font-bold text-foreground">Tippy</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <a href="/#como-funciona" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Cómo funciona
            </a>
            <a href="/#para-quien" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Para quién es
            </a>
            <a href="/#planes" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Planes
            </a>
            <Link href="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Blog
            </Link>
            <Link href="/soporte" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Soporte
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`${appUrl}/users/sign_in?utm_source=marketing&cta_location=header`}
              onClick={() => trackEvent(EVENTS.MARKETING_LOGIN_CLICKED, {
                location: 'header',
                label: 'Iniciar sesión',
                href: `${appUrl}/users/sign_in?utm_source=marketing&cta_location=header`,
              })}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Iniciar sesión
            </a>
            <a
              href={`${appUrl}/users/sign_up?utm_source=marketing&cta_location=header`}
              onClick={() => trackEvent(EVENTS.MARKETING_SIGNUP_CLICKED, {
                location: 'header',
                label: 'Comenzar ahora',
                href: `${appUrl}/users/sign_up?utm_source=marketing&cta_location=header`,
              })}
              className="inline-flex items-center justify-center rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background hover:bg-foreground/90 transition-colors"
            >
              Comenzar ahora
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-4">
              <a href="/#como-funciona" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Cómo funciona
              </a>
              <a href="/#para-quien" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Para quién es
              </a>
              <a href="/#planes" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Planes
              </a>
              <Link href="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Blog
              </Link>
              <Link href="/soporte" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Soporte
              </Link>
              <a
                href={`${appUrl}/users/sign_in?utm_source=marketing&cta_location=mobile_menu`}
                onClick={() => trackEvent(EVENTS.MARKETING_LOGIN_CLICKED, {
                  location: 'mobile_menu',
                  label: 'Iniciar sesión',
                  href: `${appUrl}/users/sign_in?utm_source=marketing&cta_location=mobile_menu`,
                })}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Iniciar sesión
              </a>
              <a
                href={`${appUrl}/users/sign_up?utm_source=marketing&cta_location=mobile_menu`}
                onClick={() => trackEvent(EVENTS.MARKETING_SIGNUP_CLICKED, {
                  location: 'mobile_menu',
                  label: 'Comenzar ahora',
                  href: `${appUrl}/users/sign_up?utm_source=marketing&cta_location=mobile_menu`,
                })}
                className="inline-flex items-center justify-center rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background hover:bg-foreground/90 transition-colors w-full"
              >
                Comenzar ahora
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

function Footer() {
  return (
    <footer className="border-t border-border bg-muted/50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src="/icon.png" alt="Tippy" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-bold text-foreground">Tippy</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">
              Tu asistente inteligente de WhatsApp para agendar citas y responder mensajes automáticamente.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/tippyagenda_ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Instagram"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61558353953472"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>

            <AppStoreLinks className="mt-5" />
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">Producto</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#como-funciona" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Cómo funciona
                </a>
              </li>
              <li>
                <a href="/#planes" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Planes
                </a>
              </li>
              <li>
                <a href="/#para-quien" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Para quién es
                </a>
              </li>
              <li>
                <Link href="/soporte" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Soporte
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-4">Contacto</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:emiliano@tippy.com.mx" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  emiliano@tippy.com.mx
                </a>
              </li>
              <li>
                <a href="/soporte" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Centro de soporte
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Política de privacidad
                </a>
              </li>
              <li>
                <a href="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Términos y condiciones
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground text-center mb-2">
            📱 App móvil disponible para iOS y Android
          </p>
          <p className="text-sm text-muted-foreground text-center">
            © {new Date().getFullYear()} Tippy. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  )
}
